// Flow Builder — Capability map
//
// Mapeia QUAIS CONTEXTOS cada trigger entrega quando dispara, e QUAIS
// CONTEXTOS cada action precisa pra funcionar.
//
// Esse mapping é a fonte única usada pelo frontend pra dar feedback visual
// ao usuário ANTES de salvar o flow — em vez de o usuário descobrir
// "ah, minha automação não rodou nada" só depois de configurar e esperar
// uma execução real.
//
// IMPORTANTE: este map é frontend-only e DEVE refletir o que os
// ActionExecutor / TriggerService fazem no backend. Quando adicionar/mudar
// uma action ou trigger, atualizar aqui também.

// Contextos possíveis: 'card', 'conversation', 'message', 'contact'.
// 'card_optional' indica que o contexto pode vir mas não é garantido —
// usuário verá warning amarelo (não vermelho).

import { normalizeTriggerSubtype } from './triggerFilterContract';

export const TRIGGER_CONTEXTS = {
  // PipelineCard belongs_to :contact is optional. The listener always delivers
  // the card and derives contact when the card actually has one.
  item_created: ['card', 'contact_optional'],
  item_moved: ['card', 'contact_optional'],
  item_won: ['card', 'contact_optional'],

  // Triggers de Conversação (entregam conversation, card é opcional)
  conversation_created: ['conversation', 'contact', 'card_optional'],
  conversation_resolved: ['conversation', 'contact', 'card_optional'],
  message_received: ['conversation', 'message', 'contact', 'card_optional'],
  first_reply_sent: ['conversation', 'contact', 'card_optional'],

  // Triggers de Contato
  contact_created: ['contact'],

  // Triggers cego (sem card, sem conversation) — exige filtros internos
  // ou só funcionam com actions que não dependem de contexto
  scheduled: [],
  webhook_received: ['webhook_payload'],

  // Appointment belongs_to :contact, while conversation and pipeline_card are
  // optional. The capability promise mirrors those model/runtime guarantees.
  appointment_created: [
    'contact',
    'conversation_optional',
    'card_optional',
    'appointment',
  ],
  appointment_confirmed: [
    'contact',
    'conversation_optional',
    'card_optional',
    'appointment',
  ],
  appointment_completed: [
    'contact',
    'conversation_optional',
    'card_optional',
    'appointment',
  ],
  appointment_no_show: [
    'contact',
    'conversation_optional',
    'card_optional',
    'appointment',
  ],
  appointment_cancelled: [
    'contact',
    'conversation_optional',
    'card_optional',
    'appointment',
  ],
  appointment_rescheduled: [
    'contact',
    'conversation_optional',
    'card_optional',
    'appointment',
  ],

  // Lead score: dispatcher should include the contact; conversation optional.
  lead_score_threshold_crossed: [
    'contact',
    'conversation_optional',
    'lead_score',
  ],
  // SLA breach: always tied to a conversation; contact via conversation.
  sla_first_response_breach: ['conversation', 'contact', 'sla'],
  sla_resolution_breach: ['conversation', 'contact', 'sla'],
  // Captain intent: arrives mid-conversation; contact via conversation.
  captain_intent_detected: ['conversation', 'contact', 'message', 'captain'],
};

// Cada action requer um conjunto MÍNIMO de contextos. Se não tiver,
// a action falha silenciosamente em runtime.
export const ACTION_REQUIREMENTS = {
  // Pipeline (precisam de card)
  move_to_stage: ['card'],
  change_priority: ['card'],
  create_pipeline_card: [], // cria card novo a partir do contact
  create_task: ['card'],
  add_tag: ['card'],

  // Conversation (precisam de conversation)
  send_message: ['conversation'],
  send_whatsapp_template: ['conversation'],
  send_whatsapp_media: ['conversation'],
  send_attachment: ['conversation'],
  send_csat_survey: ['conversation'],
  send_email_transcript: ['conversation'],
  change_status: ['conversation'],
  resolve_conversation: ['conversation'],
  reopen_conversation: ['conversation'],
  snooze_conversation: ['conversation'],
  transfer_to_inbox: ['conversation'],
  set_conversation_attribute: ['conversation'],
  mute_conversation: ['conversation'],
  unmute_conversation: ['conversation'],
  add_label: ['conversation'],
  remove_label: ['conversation'],
  assign_agent: ['conversation'],
  assign_team: ['conversation'],
  add_note: ['conversation'],

  // Contact (precisam de contact, todos os triggers exceto scheduled fornecem)
  update_contact: ['contact'],
  send_notification: [], // notifica agente, não precisa contexto
  send_email_to_team: [], // texto livre + team_id

  // Captain AI (precisa de conversation/card)
  call_captain_ai: ['conversation'],

  // Genéricas / advanced (sem requisitos rígidos)
  http_request: [],
  trigger_webhook: [],
  wait: [], // só pausa, contexto é repassado
  create_google_event: ['contact'], // usa email do contato

  // Primitivos de fluxo — sem requisitos de domínio (operam só sobre context.variables)
  set_variable: [],
  http_response: [], // requer webhook trigger pra ter efeito (warning vem do FlowValidator)
  call_automation: [], // sub-fluxo — context é repassado intacto, sem requisito local
};

// Triggers que DEVEM ser únicos no flow (não pode ter 2 do mesmo tipo
// nem 2 diferentes — leigo monta lixo se permitir).
export const TRIGGER_UNIQUENESS = 'one_per_flow';

/**
 * Dado um flow (array de nodes), descobre o trigger ativo e retorna
 * a lista de contextos que ele provê. Se não houver trigger, retorna [].
 *
 * @param {Array} nodes  Nodes do flow (do store state)
 * @returns {string[]}   Lista de contextos disponíveis
 */
/**
 * Inspeciona o gatilho do flow e classifica em qual ESTADO ele está, para o
 * frontend dar a mensagem certa em vez de um genérico "FALTA: conversa":
 *
 * - 'missing'      → não há nó de gatilho no flow
 * - 'unconfigured' → há gatilho, mas sem tipo escolhido ainda
 * - 'unknown'      → tipo escolhido não está mapeado (gap de código FE↔BE);
 *                    não dá pra validar contexto, então não acusamos falso erro
 * - 'ready'        → gatilho válido e mapeado; `contexts` lista o que entrega
 *
 * @param {Array} nodes
 * @returns {{ state: 'missing'|'unconfigured'|'unknown'|'ready', contexts: string[] }}
 */
export const triggerStatus = nodes => {
  if (!Array.isArray(nodes)) return { state: 'missing', contexts: [] };
  const triggerNode = nodes.find(n => n?.type === 'trigger');
  if (!triggerNode) return { state: 'missing', contexts: [] };
  const data = triggerNode.data || {};
  const subtype = normalizeTriggerSubtype(data);
  if (!subtype) return { state: 'unconfigured', contexts: [] };
  if (!Object.prototype.hasOwnProperty.call(TRIGGER_CONTEXTS, subtype)) {
    return { state: 'unknown', contexts: [] };
  }
  return { state: 'ready', contexts: TRIGGER_CONTEXTS[subtype] };
};

export const contextsFromFlow = nodes => triggerStatus(nodes).contexts;

/**
 * Verifica se uma action específica é compatível com os contextos
 * disponíveis no flow atual.
 *
 * @param {string}   actionType        Tipo da action (ex: 'move_to_stage')
 * @param {string[]} availableContexts Lista de contextos do trigger
 * @returns {object} { compatible: boolean, missing: string[], partial: boolean }
 *
 * `partial=true` significa que o contexto está disponível mas é opcional
 * (ex: card_optional) — usuário deve receber warning amarelo, não erro.
 */
export const checkActionCompatibility = (actionType, availableContexts) => {
  const required = ACTION_REQUIREMENTS[actionType] || [];
  if (required.length === 0)
    return { compatible: true, missing: [], partial: false };

  const missing = [];
  const partial = [];

  required.forEach(ctx => {
    if (availableContexts.includes(ctx)) return; // sólido
    if (availableContexts.includes(`${ctx}_optional`)) {
      partial.push(ctx);
      return;
    }
    missing.push(ctx);
  });

  return {
    compatible: missing.length === 0,
    missing,
    partial: missing.length === 0 && partial.length > 0,
  };
};
